import cx from 'classnames';
import React from 'react';
import { TFunction } from 'i18next';
import type { Option } from 'components/Forms/types';
import { Icon } from 'components/Icon';
import { ConnectedFormInputProps } from 'types/form-field';
import { getTranslatedSelectOptionDescription, getTranslatedSelectOptionLabel } from 'domains/language/options';

type Size = 'sm' | 'md' | 'lg';

type Props = {
  id: string;
  option: Option;
  hasError?: boolean;
  error?: string;
  size?: Size;

  tPrefixKey?: string;
  tPrefixDescriptionKey?: string;
  tFunc?: TFunction;
} & Omit<ConnectedFormInputProps<HTMLInputElement>, 'label' | 'clearValue' | 'setError' | 'setValue' | 'size'>;

const containerClasses: Record<Size, string> = {
  sm: 'text-sm',
  md: 'text-md',
  lg: 'text-lg',
};

export const CheckboxInput = React.forwardRef(function CheckboxInputWithRef(
  {
    id,
    name,
    option,
    hasError,
    error,
    disabled = false,
    defaultValue,
    size = 'sm',
    tFunc,
    tPrefixKey,
    tPrefixDescriptionKey,
    ...inputProps
  }: Props,
  ref?: React.ForwardedRef<HTMLInputElement>,
): JSX.Element {
  const { value, description } = option;
  return (
    <label htmlFor={id} className="relative block w-full">
      <input
        {...inputProps}
        ref={ref}
        id={id}
        type="checkbox"
        name={name}
        className="peer opacity-0 w-20 h-20 absolute cursor-pointer disabled:cursor-not-allowed"
        value={value}
        disabled={disabled}
      />

      <span
        className={cx(
          'absolute top-0 left-0 inline-flex items-center rounded-xs h-20 w-20 leading-none transition-all pointer-events-none',
          'text-sm border border-black-300 ring-inset text-transparent font-dazzed',
          {
            'bg-white peer-active:bg-primary-100 peer-hover:bg-purple-50 peer-hover:border-gray-400 peer-active:border-black-900 border-1 peer-checked:border-black-900 peer-checked:bg-purple-100 peer-checked:text-black-900 peer-checked:ring-1 peer-checked:ring-black-900':
              !disabled,
            'bg-black-8% peer-checked:ring-1 border-black-24% peer-checked:ring-transparent peer-checked:text-black-24%':
              disabled,
            'peer-active:bg-error-800 peer-active:border-error-500 border-error-900 peer-checked:border-error-900 peer-checked:ring-error-900 text-black':
              hasError,
          },
        )}
      >
        <Icon name="Check" className="text-inherit w-20" />
      </span>

      <span
        className={cx(
          'absolute w-20 h-20 rounded-xs top-0 left-0 outline-none ring-offset-0 transition-all pointer-events-none',
          'peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500/30 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-white',
        )}
      />

      <span
        className={`pt-2 block pl-32 font-semibold ${containerClasses[size]} text-black-900 leading-tight cursor-pointer peer-disabled:cursor-default peer-disabled:text-black-38%`}
      >
        {getTranslatedSelectOptionLabel(option, tFunc, tPrefixKey)}
      </span>

      {(description || tPrefixDescriptionKey) && (
        <span className="block pl-32 font-medium text-gray-600 text-xxs mt-4 cursor-pointer peer-disabled:cursor-default peer-disabled:text-black-38%">
          {getTranslatedSelectOptionDescription(option, tFunc, tPrefixDescriptionKey)}
        </span>
      )}

      {error && (
        <span className="block pl-32 font-semibold text-red-900 text-xxs mt-2 cursor-pointer peer-disabled:cursor-default">
          {error}
        </span>
      )}
    </label>
  );
});
